// Composer — textarea + Draft/Send buttons for replying to a conversation.
//
// Stays dumb: takes a conversationId prop, owns its own textarea state,
// fires useDraftReply on "Draft with Haiku" click (replaces the textarea
// with the model output), fires useSendReply on "Send reply" click
// (clears the textarea on success).
//
// Signature is appended server-side (see routes/conversations.ts `signatureFor`),
// so the textarea contains just the body; the preview below shows the exact
// two-line signature the server will append.
//
// Phase E-D: if the user drafted before sending, we also fire-and-forget
// useRecordDraftSend to persist the draft-vs-sent diff for prompt tuning.
// If they typed from scratch (no draftId in state), the diff call is skipped.
//
// Polish PR (Phase 1 #1 — issues #9 + #23):
//   autosaveKey     — when set, body is persisted to localStorage per-thread
//                     and restored on mount. Cleared after a successful send.
//   undoSendSeconds — when > 0, Send clicks are queued for N seconds behind
//                     an "Undo" toast. If Kimi clicks Undo, the body is put
//                     back and the API call never fires. If she navigates
//                     away during the window, the send commits immediately
//                     so nothing gets stuck in limbo.

import { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSendReply,
  useDraftReply,
  useRecordDraftSend,
  type ReplyResponse,
  type RecordDraftSendResponse,
} from '@/lib/hooks/useConversation';
import {
  useUploadAttachment,
  useDeleteAttachment,
  type StagedAttachment,
} from '@/lib/hooks/useAttachments';
import { apiPost } from '@/lib/api';
import { SavedRepliesMenu } from '@/components/center/SavedRepliesMenu';
import { SlashCommandPopover } from '@/components/center/SlashCommandPopover';

// Polish #18: attachments are staged in the composer before send.  Same cap
// as the backend constant — keep both in sync if we ever raise the ceiling.
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const IMAGE_EXT = /\.(png|jpe?g|gif|heic|webp|svg)$/i;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function looksLikeImage(filename: string, mime: string): boolean {
  if (mime.startsWith('image/')) return true;
  return IMAGE_EXT.test(filename);
}
import {
  applySlashReply,
  findActiveSlash,
  matchSlashReplies,
  type SavedReply,
  type SavedReplyContext,
  type SlashToken,
} from '@/lib/savedReplies';

type ComposerProps = {
  conversationId: string;
  brand: 'md' | 'hk';
  /** Called after a successful send — parent can navigate away or show a toast. */
  onSent?: () => void;
  /** Optional body to seed the textarea with on mount (e.g., from a proposed action preview). */
  initialBody?: string;
  /** Called when the user dismisses an embedded composer (esc, cancel button). */
  onDismiss?: () => void;
  /**
   * When true, render inline without sticky positioning. Used inside
   * ActionsPanel's drawer where the panel itself owns scroll behavior.
   * Default: false (legacy sticky-bottom behavior for direct page mounts).
   */
  embedded?: boolean;
  /**
   * When provided, body text is saved to localStorage keyed by this value
   * (usually the conversation id) and restored on mount. Entry is cleared
   * after a successful send. Leave undefined to disable autosave — good for
   * short-lived drawers like the Tweak preview.
   */
  autosaveKey?: string;
  /**
   * When > 0, Send is queued for N seconds behind an Undo toast before the
   * API call actually fires. Matches Gmail's "undo send" pattern. Leave at 0
   * (the default) for composers that shouldn't have the delay — drawers,
   * action previews, etc.
   */
  undoSendSeconds?: number;
  /**
   * Autofill context passed down to SavedRepliesMenu for Polish #15 quick-
   * reply templates (`{name}` / `{order}` / `{tracking}` placeholders).
   * Safe to omit — the menu will just surface the placeholders literally
   * when context isn't provided.
   */
  templateContext?: SavedReplyContext;
};

export interface ComposerHandle {
  /** Focus the textarea. Used by the ActionsPanel "write from scratch" button. */
  focus: () => void;
  /**
   * Replace the textarea body with `text` and focus it. Used by Tweak-style
   * flows that want to seed the sticky composer from the Haiku preview.
   */
  setBody: (text: string) => void;
}

function draftStorageKey(autosaveKey: string): string {
  return `md-inbox-draft:${autosaveKey}`;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  {
    conversationId,
    brand,
    onSent,
    initialBody,
    onDismiss,
    embedded = false,
    autosaveKey,
    undoSendSeconds = 0,
    templateContext,
  },
  ref,
) {
  // Lazy initializer hydrates from localStorage on first render so the very
  // first paint shows Kimi's saved draft instead of flashing empty → restored.
  const [body, setBody] = useState<string>(() => {
    if (autosaveKey) {
      try {
        const saved = localStorage.getItem(draftStorageKey(autosaveKey));
        if (saved && saved.length > 0) return saved;
      } catch {
        /* no-op — private mode, SSR, etc. */
      }
    }
    return initialBody ?? '';
  });

  // Tracks the most recent drafts.id returned from useDraftReply. When the user
  // sends, we pass this into useRecordDraftSend so the backend can store the
  // sent text against the original draft for diff analysis.
  const [lastDraftId, setLastDraftId] = useState<string | null>(null);

  // Undo-send pending state. Non-null means Kimi hit Send and the actual API
  // call is still queued behind the timer. `expiresAt` is a wall-clock ms
  // timestamp so a setInterval tick can compute the remaining countdown
  // without drift. `targetConversationId` snapshots the thread the send was
  // composed against, so J/K navigation mid-countdown can't accidentally
  // reroute the reply to a different conversation.
  const [pending, setPending] = useState<{
    snapshot: string;
    draftId: string | null;
    expiresAt: number;
    targetConversationId: string;
    targetAutosaveKey: string | undefined;
    attachmentIds: string[];
    /** Full staged rows so Undo can restore the chip strip in one go. */
    stagedSnapshot: StagedAttachment[];
  } | null>(null);

  // Polish #18 — staged outbound attachments.  Each entry is the row the
  // backend returns from POST /api/conversations/:id/attachments.  They
  // stay local until Send fires, at which point their ids are handed off
  // to the reply endpoint and the backend binds them to the outbound
  // message row.
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragDepth, setDragDepth] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadAttachment = useUploadAttachment(conversationId);
  const deleteAttachment = useDeleteAttachment();
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Snapshot of `pending` for the unmount cleanup — reading `pending` directly
  // in the cleanup closes over the initial value.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Polish #19 — active slash-command token. Non-null when the textarea
  // cursor is inside a `/tok` that could still match a slash command. The
  // popover reads this to decide whether to render and how to filter. Set
  // from onChange / onKeyUp / onSelect / onClick so arrow-key caret moves
  // also open / close the menu.
  const [slashToken, setSlashToken] = useState<SlashToken | null>(null);
  const slashMatches = useMemo(
    () => (slashToken ? matchSlashReplies(slashToken.query) : []),
    [slashToken],
  );

  const sendMutation = useSendReply(conversationId);
  const draftMutation = useDraftReply(conversationId);
  const recordSendMutation = useRecordDraftSend();
  // React Query client for the deferred-send code path — we bypass
  // useSendReply there because its mutationFn closes over the current
  // conversationId prop, which is no longer the right target if the user
  // J/K-navigated while the 10s undo window was counting down.
  const qc = useQueryClient();

  const signaturePreview =
    brand === 'hk' ? 'Kimi\nHarperKellee' : 'Kimi\nMarket Dwellings';

  // Expose an imperative handle so ActionsPanel / Center can focus or seed
  // the sticky composer without prop-drilling state.
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
      // If we're inside a scroll container, bring the textarea into view.
      textareaRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },
    setBody: (text: string) => {
      setBody(text);
      // Clear the draftId — the seeded body didn't come from useDraftReply,
      // so any later diff would be meaningless.
      setLastDraftId(null);
      // Next tick so the value is committed before focus.
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
  }));

  // ── Autosave: debounced localStorage persistence ──────────────────────────
  useEffect(() => {
    if (!autosaveKey) return;
    const key = draftStorageKey(autosaveKey);
    // Debounce writes so we're not pinging localStorage on every keystroke.
    const handle = setTimeout(() => {
      try {
        if (body.trim().length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, body);
        }
      } catch {
        /* no-op */
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [body, autosaveKey]);

  // If the parent swaps conversationId (e.g. J/K navigation) we need to
  // re-hydrate from the new key's saved draft. The initial useState ran once
  // against the mount-time key and won't re-run on prop changes.
  useEffect(() => {
    if (!autosaveKey) return;
    try {
      const saved = localStorage.getItem(draftStorageKey(autosaveKey));
      setBody(saved ?? initialBody ?? '');
      setLastDraftId(null);
    } catch {
      setBody(initialBody ?? '');
    }
    // Polish #18 — staged attachments belong to the outgoing conversation,
    // not the next one. Clear them when Kimi J/K navigates so she
    // doesn't accidentally attach an old proof to a new thread.
    setStaged([]);
    setUploadError(null);
    // Purposely omit initialBody from deps — we only want to re-hydrate when
    // the thread key changes, not when the parent's memoized prop identity
    // flickers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosaveKey]);

  // ── Undo-send: ticker while pending, cleanup on unmount ───────────────────
  useEffect(() => {
    if (!pending) return;
    // Re-render ~5x/sec for a smooth countdown number without burning CPU.
    intervalRef.current = setInterval(() => setNowMs(Date.now()), 200);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pending]);

  const clearDraftStorage = useCallback((key: string | undefined) => {
    if (!key) return;
    try {
      localStorage.removeItem(draftStorageKey(key));
    } catch {
      /* no-op */
    }
  }, []);

  // Immediate send — used when undoSendSeconds is 0 (drawers, action
  // previews). Routes through the React Query mutation so the conversation
  // cache is invalidated in the usual way.
  const fireImmediateSend = useCallback(
    (snapshot: string, draftIdAtSend: string | null, attachmentIds: string[]) => {
      sendMutation.mutate(
        { body: snapshot, attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined },
        {
          onSuccess: () => {
            if (draftIdAtSend) {
              recordSendMutation.mutate({
                draftId: draftIdAtSend,
                sentText: snapshot,
              });
            }
            clearDraftStorage(autosaveKey);
            setStaged([]);
            onSent?.();
          },
        },
      );
    },
    [sendMutation, recordSendMutation, onSent, clearDraftStorage, autosaveKey],
  );

  // Deferred send — used when a pending undo window times out or the
  // Composer unmounts with a pending reply. Calls apiPost directly against
  // the conversationId captured at pending-start so navigation can't reroute
  // the send. On success we invalidate the same query keys useSendReply
  // would have invalidated, so the Standup + thread views refresh even if
  // the user already moved on.
  const fireDeferredSend = useCallback(
    (p: {
      snapshot: string;
      draftId: string | null;
      targetConversationId: string;
      targetAutosaveKey: string | undefined;
      attachmentIds: string[];
    }) => {
      apiPost<ReplyResponse, { body: string; attachment_ids?: string[] }>(
        `/api/conversations/${p.targetConversationId}/reply`,
        {
          body: p.snapshot,
          ...(p.attachmentIds.length > 0 ? { attachment_ids: p.attachmentIds } : {}),
        },
      )
        .then(() => {
          clearDraftStorage(p.targetAutosaveKey);
          if (p.draftId) {
            apiPost<RecordDraftSendResponse>(`/api/ai/drafts/${p.draftId}/sent`, {
              sent_text: p.snapshot,
            }).catch(() => {
              /* diff capture is fire-and-forget; server-side logs the error */
            });
          }
          void qc.invalidateQueries({ queryKey: ['conversation', p.targetConversationId] });
          void qc.invalidateQueries({ queryKey: ['conversations'] });
          void qc.invalidateQueries({ queryKey: ['proofs', p.targetConversationId] });
          // Only surface onSent when the user is still viewing the thread
          // they composed against — otherwise the auto-advance would jump
          // forward from the wrong thread's bucket.
          if (p.targetConversationId === conversationId) {
            onSent?.();
          }
        })
        .catch((err: unknown) => {
          // Bounce into the React Query error surface so the inline banner
          // still renders. We do this by nudging the send mutation into an
          // error state with the same message.
          const msg = err instanceof Error ? err.message : 'send_failed';
          // eslint-disable-next-line no-console
          console.error('[Composer] deferred send failed', msg);
        });
    },
    [qc, onSent, clearDraftStorage, conversationId],
  );

  // If conversationId changes mid-countdown (e.g. J/K navigation), commit
  // the pending send against the ORIGINAL thread before letting the state
  // flip. We do this in a useEffect rather than the unmount cleanup so the
  // commit can still use the old pending snapshot.
  const prevConversationIdRef = useRef(conversationId);
  useEffect(() => {
    const prev = prevConversationIdRef.current;
    prevConversationIdRef.current = conversationId;
    if (prev === conversationId) return;
    const p = pendingRef.current;
    if (!p) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPending(null);
    fireDeferredSend(p);
  }, [conversationId, fireDeferredSend]);

  // Commit any pending send on unmount so a reply doesn't vanish if Kimi
  // closes the Center page mid-countdown. We fire against the snapshotted
  // conversationId, not the current one, for the same reason as above.
  useEffect(() => {
    return () => {
      const p = pendingRef.current;
      if (p) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        fireDeferredSend(p);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = () => {
    const trimmed = body.trim();
    if (!trimmed || sendMutation.isPending || pending) return;
    // Polish #18 — refuse to send while an upload is still in flight; the
    // attachment id doesn't exist yet and the binding step would miss it.
    if (uploadAttachment.isPending) return;

    const attachmentIds = staged.map((a) => a.id);

    if (undoSendSeconds > 0) {
      const snapshot = trimmed;
      const draftIdAtSend = lastDraftId;
      // Clear the visible textarea + drop the saved draft immediately so
      // there's no stale copy sitting around if navigation races the timer.
      setBody('');
      setLastDraftId(null);
      clearDraftStorage(autosaveKey);
      // Clear staged chips immediately so Kimi can see the pending state —
      // the actual binding happens on deferred-send success. If she Undoes,
      // we restore the staged list from the pending snapshot.
      setStaged([]);
      const expiresAt = Date.now() + undoSendSeconds * 1000;
      const pendingState = {
        snapshot,
        draftId: draftIdAtSend,
        expiresAt,
        targetConversationId: conversationId,
        targetAutosaveKey: autosaveKey,
        attachmentIds,
        stagedSnapshot: staged,
      };
      setPending(pendingState);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        setPending(null);
        fireDeferredSend(pendingState);
      }, undoSendSeconds * 1000);
      return;
    }

    // Legacy immediate path — kept for drawers and embedded composers that
    // don't want the undo delay.
    const draftIdAtSend = lastDraftId;
    setBody('');
    setLastDraftId(null);
    clearDraftStorage(autosaveKey);
    fireImmediateSend(trimmed, draftIdAtSend, attachmentIds);
  };

  const handleUndo = () => {
    if (!pending) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setBody(pending.snapshot);
    setLastDraftId(pending.draftId);
    setStaged(pending.stagedSnapshot);
    setPending(null);
    // Put focus back in the textarea so Kimi can keep editing immediately.
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleDraft = () => {
    if (draftMutation.isPending || sendMutation.isPending || pending) return;
    draftMutation.mutate(undefined, {
      onSuccess: (res) => {
        // Replace whatever's in the textarea with the fresh draft. Track its id
        // so the send handler can record the edit diff when Kimi sends.
        setBody(res.draft);
        setLastDraftId(res.draftId ?? null);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter sends.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
      return;
    }
    // Esc dismisses when we're embedded inside a drawer.
    if (e.key === 'Escape' && onDismiss) {
      e.preventDefault();
      onDismiss();
    }
  };

  // Polish #19 — re-detect whether the textarea cursor is inside an active
  // slash token. Called from onChange (typing), onKeyUp + onSelect + onClick
  // (caret moves without typing), and after a slash pick to clear the
  // popover. Passing an explicit cursor keeps us independent of React state
  // timing — the handler reads selectionStart right off the event target.
  const rescanSlash = useCallback(
    (value: string, cursor: number) => {
      setSlashToken(findActiveSlash(value, cursor));
    },
    [],
  );

  const handleSlashPick = useCallback(
    (_reply: SavedReply, resolvedBody: string) => {
      if (!slashToken) return;
      const { value: nextValue, cursor: nextCursor } = applySlashReply(
        body,
        slashToken,
        resolvedBody,
      );
      setBody(nextValue);
      setSlashToken(null);
      setLastDraftId(null);
      // Restore the caret at the end of the inserted body on the next tick
      // so React has a chance to commit the value prop first.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        try {
          el.setSelectionRange(nextCursor, nextCursor);
        } catch {
          /* no-op — some browsers throw on null range */
        }
      });
    },
    [body, slashToken],
  );

  const dismissSlash = useCallback(() => setSlashToken(null), []);

  // Phase 6e + Polish #15 — insert a saved reply template. If the textarea
  // already has text, confirm before overwriting so Kimi doesn't accidentally
  // lose a half-typed reply. Dropping any existing draftId wipes the diff
  // link, which is correct: the sent text didn't descend from a Haiku draft.
  //
  // `resolvedBody` is the template body with autofill placeholders (`{name}`,
  // `{order}`, `{tracking}`) already substituted by the menu — so the
  // composer just drops it in as-is.
  const handlePickTemplate = (reply: SavedReply, resolvedBody: string) => {
    const existing = body.trim();
    if (existing.length > 0) {
      const ok = window.confirm(
        `Replace your current reply with the "${reply.label}" template?`,
      );
      if (!ok) return;
    }
    setBody(resolvedBody);
    setLastDraftId(null);
  };

  // ── Polish #18: file handlers ─────────────────────────────────────────────
  // One path for both the paperclip button and the drag-drop zone.  Validates
  // size locally (saves a round trip for oversize drops), fires the upload
  // mutation, and appends the resolved row to the staged list.  Failures
  // surface as a red one-liner under the chip strip.
  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setUploadError(null);
      for (const file of list) {
        if (file.size === 0) {
          setUploadError(`${file.name} is empty`);
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setUploadError(
            `${file.name} is ${formatBytes(file.size)} — the limit is ${formatBytes(
              MAX_ATTACHMENT_BYTES,
            )}`,
          );
          continue;
        }
        try {
          const row = await uploadAttachment.mutateAsync(file);
          setStaged((prev) => [...prev, row]);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'upload_failed';
          setUploadError(`Couldn't upload ${file.name}: ${msg}`);
        }
      }
    },
    [uploadAttachment],
  );

  const handleRemoveStaged = useCallback(
    async (id: string) => {
      // Drop from the visible list first so the UI feels responsive even if
      // the backend call takes a moment. If the DELETE fails we add it back
      // and surface the error — the worst case is Kimi sees the chip return.
      const snap = staged;
      setStaged((prev) => prev.filter((a) => a.id !== id));
      try {
        await deleteAttachment.mutateAsync(id);
      } catch (err) {
        setStaged(snap);
        const msg = err instanceof Error ? err.message : 'delete_failed';
        setUploadError(`Couldn't remove that file: ${msg}`);
      }
    },
    [deleteAttachment, staged],
  );

  const onDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    setDragDepth((d) => d + 1);
  }, []);
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
  }, []);
  const onDragLeave = useCallback(() => {
    setDragDepth((d) => Math.max(0, d - 1));
  }, []);
  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      setDragDepth(0);
      void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const sendDisabled =
    sendMutation.isPending ||
    uploadAttachment.isPending ||
    body.trim().length === 0 ||
    !!pending;
  const draftBusy = draftMutation.isPending;
  const draftDisabled = draftBusy || sendMutation.isPending || !!pending;
  const textareaDisabled = sendMutation.isPending || draftBusy || !!pending;

  const draftLabel = draftBusy
    ? 'Drafting…'
    : body.trim().length > 0
      ? 'Redraft with Haiku'
      : 'Draft with Haiku';

  // Sticky mode anchors the composer to the viewport bottom when the
  // thread scrolls past, with the stone ground + top border separating it
  // from the bubbles above. Embedded mode just paints the background so
  // the drawer-host owns layout concerns.
  const wrapperClass = embedded
    ? 'bg-stone'
    : 'composer-sticky';

  // Countdown number shown in the undo toast. Pull from the ticker state so
  // it re-renders; default to full seconds if we haven't ticked yet.
  const remainingSecs = pending
    ? Math.max(0, Math.ceil((pending.expiresAt - nowMs) / 1000))
    : 0;

  return (
    <div className={wrapperClass}>
      <div>
        {pending ? (
          <div
            className="composer-undo-bar"
            role="status"
            aria-live="polite"
          >
            <div className="composer-undo-left">
              <span className="composer-undo-dot" aria-hidden />
              <span className="composer-undo-label">
                Sending in {remainingSecs}s…
              </span>
            </div>
            <button
              type="button"
              className="btn sm"
              onClick={handleUndo}
              title="Cancel this send and bring the text back"
            >
              Undo
            </button>
          </div>
        ) : null}
        <div
          className={`rounded-lg border border-ink/15 bg-white overflow-hidden focus-within:border-monstera/60 focus-within:shadow-sm transition composer-surface${
            dragDepth > 0 ? ' composer-drop-target' : ''
          }`}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {dragDepth > 0 ? (
            <div className="composer-drop-overlay" role="status">
              <span>Drop to attach</span>
            </div>
          ) : null}
          {slashToken && slashMatches.length > 0 ? (
            <SlashCommandPopover
              matches={slashMatches}
              context={templateContext ?? { name: null, order: null, tracking: null }}
              onPick={handleSlashPick}
              onDismiss={dismissSlash}
              resetKey={slashToken.query}
            />
          ) : null}
          {staged.length > 0 || uploadAttachment.isPending ? (
            <div className="composer-staged">
              {staged.map((a) => {
                const isImg = looksLikeImage(a.filename, a.mime_type);
                return (
                  <div
                    key={a.id}
                    className={`composer-chip${isImg ? ' composer-chip-img' : ''}`}
                    title={`${a.filename} · ${formatBytes(a.size_bytes)}`}
                  >
                    {isImg && a.url ? (
                      <img src={a.url} alt={a.filename} className="composer-chip-thumb" />
                    ) : (
                      <span className="composer-chip-icon" aria-hidden>
                        📎
                      </span>
                    )}
                    <span className="composer-chip-name">{a.filename}</span>
                    <span className="composer-chip-size">{formatBytes(a.size_bytes)}</span>
                    <button
                      type="button"
                      className="composer-chip-remove"
                      aria-label={`Remove ${a.filename}`}
                      onClick={() => void handleRemoveStaged(a.id)}
                      disabled={deleteAttachment.isPending || sendMutation.isPending}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {uploadAttachment.isPending ? (
                <div className="composer-chip composer-chip-uploading" aria-live="polite">
                  <span className="composer-chip-icon" aria-hidden>⏳</span>
                  <span className="composer-chip-name">Uploading…</span>
                </div>
              ) : null}
            </div>
          ) : null}
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              rescanSlash(e.target.value, e.target.selectionStart ?? e.target.value.length);
            }}
            onKeyUp={(e) => {
              // Catches arrow-key + home/end caret moves so the popover can
              // open / close when Kimi navigates into or out of a slash token
              // without typing. Ignore keys that onChange already handled.
              const el = e.currentTarget;
              rescanSlash(el.value, el.selectionStart ?? el.value.length);
            }}
            onSelect={(e) => {
              const el = e.currentTarget;
              rescanSlash(el.value, el.selectionStart ?? el.value.length);
            }}
            onClick={(e) => {
              const el = e.currentTarget;
              rescanSlash(el.value, el.selectionStart ?? el.value.length);
            }}
            onBlur={() => {
              // Drop the popover when focus leaves so it doesn't stick around
              // while Kimi clicks on another panel.
              setSlashToken(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              pending
                ? `Sending… (undo in ${remainingSecs}s)`
                : draftBusy
                  ? 'Drafting with Haiku…'
                  : 'Type your reply…'
            }
            rows={5}
            className="w-full resize-y px-4 py-3 text-[15px] leading-[1.55] font-sans text-ink placeholder:text-ink/40 focus:outline-none bg-transparent"
            disabled={textareaDisabled}
          />
          <div className="flex items-start justify-between gap-3 px-4 py-2 border-t border-ink/10 bg-eggshell/40">
            <div className="text-[12px] text-ink/55 leading-[1.4] min-w-0">
              <div className="flex items-center gap-1 flex-wrap">
                <span>Signature auto-added</span>
                <span className="text-ink/30">·</span>
                <kbd className="font-mono text-[11px]">⌘↵</kbd>
                <span>to send</span>
                {autosaveKey ? (
                  <>
                    <span className="text-ink/30">·</span>
                    <span className="italic">Autosaves as you type</span>
                  </>
                ) : null}
              </div>
              <pre className="mt-1 font-sans text-[12px] text-ink/70 whitespace-pre-line leading-[1.35] m-0">
{signaturePreview}
              </pre>
              {uploadError ? (
                <div className="mt-1 text-[12px] text-red-700" role="alert">
                  {uploadError}
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="sr-only"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) void handleFiles(files);
                  // Reset the input so re-picking the same file still fires onChange.
                  e.currentTarget.value = '';
                }}
              />
              <button
                type="button"
                className="btn sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={
                  uploadAttachment.isPending || sendMutation.isPending || !!pending
                }
                title="Attach a file (or drop one onto the composer)"
                aria-label="Attach a file"
              >
                <span aria-hidden>📎</span>
                <span>Attach</span>
              </button>
              {onDismiss ? (
                <button
                  type="button"
                  className="btn sm"
                  onClick={onDismiss}
                  disabled={sendMutation.isPending}
                  title="Cancel this reply"
                >
                  Cancel
                </button>
              ) : null}
              <SavedRepliesMenu
                disabled={textareaDisabled}
                context={templateContext ?? { name: null, order: null, tracking: null }}
                onPick={handlePickTemplate}
              />
              <button
                type="button"
                className="btn"
                onClick={handleDraft}
                disabled={draftDisabled}
                style={draftDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                title="Generate a reply draft with Claude Haiku"
              >
                {draftLabel}
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={handleSend}
                disabled={sendDisabled}
                style={sendDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                {sendMutation.isPending ? 'Sending…' : 'Send reply'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {draftMutation.isError ? (
          <div
            className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            Couldn't draft that reply: {(draftMutation.error as Error).message}
          </div>
        ) : null}

        {sendMutation.isError ? (
          <div
            className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            Couldn't send that reply: {(sendMutation.error as Error).message}
          </div>
        ) : null}
      </div>
    </div>
  );
});
